var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { createElement as _createElement } from "react";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Autocomplete, Box, Chip, FormControl, FormHelperText, TextField, } from "@mui/material";
import { buildFieldValidation } from "../../validators/field-components";
import { getElementValueKey, reorderChoices } from "../../../../utils/helpers";
import CommentArea from "../common/CommentArea";
import { useElementConditions } from "../../context/FormLogicContext";
import Header from "../common/Header";
import { getCommentFieldName, getInitialValueForElement, } from "../../../../utils/forms-functions";
import { NONE_VALUE, SELECT_ALL_VALUE } from "./constants/special-options";
import { useClearInvisibleValue } from "../../hooks/useClearInvisibleValue";
import { useChoicesConditions } from "../../hooks/useChoicesConditions";
/**
 * Detecta se há um valor customizado vindo do input text "other" no array de valores
 */
var findOtherValueInArray = function (currentValues, baseChoicesValues, otherTextValue) {
    return currentValues.find(function (v) {
        return !baseChoicesValues.includes(v) &&
            v !== otherTextValue &&
            v !== NONE_VALUE &&
            v !== SELECT_ALL_VALUE;
    });
};
/**
 * Converte os valores do formulário (strings) para o formato esperado pelo Autocomplete (IChoiceValueText[])
 */
var convertFormValuesToAutocompleteValue = function (currentValues, choices, baseChoicesValues, otherTextValue, isNoneSelected, isSelectAllSelected, isOtherSelected, otherValueInArray) {
    if (isNoneSelected) {
        var noneChoice = choices.find(function (c) { return c.value === NONE_VALUE; });
        return noneChoice ? [noneChoice] : [];
    }
    if (isSelectAllSelected) {
        return choices.filter(function (c) { return baseChoicesValues.includes(c.value); });
    }
    var mappedValues = currentValues
        .map(function (value) {
        if (value === SELECT_ALL_VALUE) {
            return null;
        }
        if (value === otherTextValue) {
            return {
                value: otherTextValue,
                text: otherTextValue,
            };
        }
        if (otherValueInArray && value === otherValueInArray) {
            return {
                value: otherValueInArray,
                text: otherValueInArray,
            };
        }
        return choices.find(function (choice) { return choice.value === value; }) || null;
    })
        .filter(function (v) {
        return v !== undefined && v !== null;
    });
    if (!isOtherSelected) {
        return mappedValues;
    }
    if (otherValueInArray) {
        return mappedValues.map(function (object) {
            return object.value === otherValueInArray
                ? { value: otherTextValue, text: otherTextValue }
                : object;
        });
    }
    return mappedValues;
};
var processAutocompleteChange = function (newValue, baseChoicesValues, selectableChoicesValues, otherTextValue, maxSelectedChoices, isOtherSelected, otherValueInArray, otherValue, element, fieldOnChange, setOtherValue) {
    var newValues = newValue.map(function (choice) { return choice.value; });
    if (newValues.includes(NONE_VALUE)) {
        fieldOnChange([NONE_VALUE]);
        return;
    }
    var valuesWithoutNone = newValues.filter(function (value) { return value !== NONE_VALUE; });
    if (valuesWithoutNone.includes(SELECT_ALL_VALUE)) {
        if (selectableChoicesValues.length === 0 ||
            (maxSelectedChoices !== undefined &&
                selectableChoicesValues.length > maxSelectedChoices)) {
            return;
        }
        fieldOnChange(selectableChoicesValues);
        return;
    }
    var valuesWithoutSelectAll = valuesWithoutNone.filter(function (value) { return value !== SELECT_ALL_VALUE; });
    var wasOtherSelected = isOtherSelected;
    var isOtherStillSelected = element.showOtherItem && valuesWithoutSelectAll.includes(otherTextValue);
    // Caso 1: "other" foi desmarcado - limpa o estado e remove valores customizados do input text "other"
    if (wasOtherSelected && !isOtherStillSelected) {
        setOtherValue("");
        var cleanedValues = valuesWithoutSelectAll.filter(function (value) {
            return baseChoicesValues.includes(value);
        });
        fieldOnChange(cleanedValues);
        return;
    }
    // Caso 2: "other" foi selecionado pela primeira vez - usa o valor do input text "other" customizado se existir
    if (!wasOtherSelected &&
        element.showOtherItem &&
        valuesWithoutSelectAll.includes(otherTextValue)) {
        var finalValue = otherValue.trim() || otherTextValue;
        var valuesWithoutOther = valuesWithoutSelectAll.filter(function (value) { return value !== otherTextValue; });
        var finalValues = __spreadArray(__spreadArray([], valuesWithoutOther, true), [finalValue], false);
        fieldOnChange(finalValues);
        return;
    }
    // Caso 3: "other" continua selecionado - mantém o valor do input text "other" customizado se existir
    if (wasOtherSelected && isOtherStillSelected) {
        var finalOtherValue = otherValueInArray || otherValue.trim() || otherTextValue;
        var valuesWithoutOther = valuesWithoutSelectAll.filter(function (value) { return value !== otherTextValue; });
        var finalValues = __spreadArray(__spreadArray([], valuesWithoutOther, true), [finalOtherValue], false);
        fieldOnChange(finalValues);
        return;
    }
    // Caso padrão: apenas atualiza os valores sem "none" e "selectAll"
    fieldOnChange(valuesWithoutSelectAll);
};
var TagboxField = function (_a) {
    var element = _a.element, errors = _a.errors;
    var _b = useFormContext(), unregister = _b.unregister, getValues = _b.getValues, setValue = _b.setValue;
    var valueKey = getElementValueKey(element);
    var error = errors[valueKey];
    var _c = useElementConditions(element), isVisible = _c.isVisible, isEnabled = _c.isEnabled, isInteractive = _c.isInteractive, fieldRequired = _c.isRequired;
    var _d = useChoicesConditions(element), choicesVisible = _d.choicesVisible, choicesEnabled = _d.choicesEnabled;
    var validation = useMemo(function () {
        return buildFieldValidation(element, {
            isRequired: fieldRequired,
            readOnly: !isEnabled,
        });
    }, [element, fieldRequired, isEnabled]);
    var valueKeys = useMemo(function () {
        return element.showCommentArea
            ? [valueKey, getCommentFieldName(valueKey)]
            : [valueKey];
    }, [valueKey, element.showCommentArea]);
    useClearInvisibleValue({
        isVisible: isVisible,
        valueKeys: valueKeys,
        unregister: unregister,
    });
    var otherTextValue = element.showOtherItem
        ? element.otherText || "Outro"
        : "";
    var choices = useMemo(function () {
        var baseChoices = (element.choices || []);
        var choicesFormatted = baseChoices.map(function (choice) {
            if (typeof choice === "string") {
                return { value: choice, text: choice };
            }
            return choice;
        });
        var orderedChoices = choicesFormatted;
        if (element.choicesOrder) {
            orderedChoices = reorderChoices(orderedChoices, element.choicesOrder);
        }
        var finalChoices = __spreadArray([], orderedChoices, true);
        if (element.showSelectAllItem) {
            finalChoices = __spreadArray([
                {
                    value: SELECT_ALL_VALUE,
                    text: element.selectAllText || "Selecionar todos",
                }
            ], finalChoices, true);
        }
        if (element.showNoneItem) {
            finalChoices = __spreadArray(__spreadArray([], finalChoices, true), [
                { value: NONE_VALUE, text: element.noneText || "Nenhum" },
            ], false);
        }
        if (element.showOtherItem) {
            finalChoices = __spreadArray(__spreadArray([], finalChoices, true), [
                {
                    value: otherTextValue,
                    text: otherTextValue,
                },
            ], false);
        }
        return finalChoices;
    }, [element, otherTextValue]);
    var _e = useState(""), otherValue = _e[0], setOtherValue = _e[1];
    var baseChoicesValues = useMemo(function () {
        var baseChoices = (element.choices || []);
        return baseChoices.map(function (choice) {
            return typeof choice === "string" ? choice : choice.value;
        });
    }, [element.choices]);
    var selectableChoicesValues = useMemo(function () { return (choicesVisible && choicesEnabled ? baseChoicesValues : []); }, [choicesVisible, choicesEnabled, baseChoicesValues]);
    useEffect(function () {
        if (!choicesVisible) {
            var currentValues = (getValues(valueKey) || []);
            var filteredValues = currentValues.filter(function (value) { return !baseChoicesValues.includes(value); });
            if (filteredValues.length !== currentValues.length) {
                setValue(valueKey, filteredValues);
            }
        }
    }, [choicesVisible, baseChoicesValues, valueKey, getValues, setValue]);
    var initialValue = getInitialValueForElement(element);
    return (_jsx(_Fragment, { children: isVisible ? (_jsx(Box, __assign({ className: "component-wrapper" }, { children: _jsxs(FormControl, __assign({ fullWidth: true, error: !!error, sx: { mb: 3 } }, { children: [_jsx(Header, { element: element, error: !!error, isRequired: fieldRequired }), _jsx(Controller, { name: valueKey, defaultValue: initialValue, rules: {
                            validate: function (value) {
                                var _a;
                                var result = validation.safeParse(value);
                                if (!result.success) {
                                    return ((_a = result.error.errors[0]) === null || _a === void 0 ? void 0 : _a.message) || "Valor inválido";
                                }
                                return true;
                            },
                        }, render: function (_a) {
                            var field = _a.field;
                            var currentValues = field.value || [];
                            var isNoneSelected = currentValues.includes(NONE_VALUE);
                            var isSelectAllSelected = currentValues.includes(SELECT_ALL_VALUE);
                            var otherValueInArray = findOtherValueInArray(currentValues, baseChoicesValues, otherTextValue);
                            var isOtherSelected = element.showOtherItem &&
                                (currentValues.includes(otherTextValue) ||
                                    !!otherValueInArray);
                            var selectedChoicesCount = currentValues.filter(function (value) { return value !== SELECT_ALL_VALUE; }).length;
                            var maxSelectedChoices = element.maxSelectedChoices;
                            var hasReachedMaxSelectedChoices = maxSelectedChoices !== undefined &&
                                selectedChoicesCount >= maxSelectedChoices;
                            var cannotSelectAll = selectableChoicesValues.length === 0 ||
                                (maxSelectedChoices !== undefined &&
                                    selectableChoicesValues.length > maxSelectedChoices);
                            var displayOtherValue = otherValueInArray || otherValue;
                            var autocompleteValue = convertFormValuesToAutocompleteValue(currentValues, choices, baseChoicesValues, otherTextValue, isNoneSelected, isSelectAllSelected, isOtherSelected, otherValueInArray);
                            var handleAutocompleteChange = function (_, newValue) {
                                var nextSelectedChoicesCount = newValue.filter(function (choice) { return choice.value !== SELECT_ALL_VALUE; }).length;
                                if (maxSelectedChoices !== undefined &&
                                    nextSelectedChoicesCount > maxSelectedChoices) {
                                    return;
                                }
                                processAutocompleteChange(newValue, baseChoicesValues, selectableChoicesValues, otherTextValue, maxSelectedChoices, isOtherSelected, otherValueInArray, otherValue, element, field.onChange, setOtherValue);
                            };
                            var handleOtherInputChange = function (e) {
                                var inputValue = e.target.value;
                                setOtherValue(inputValue);
                                var currentValues = field.value || [];
                                var valuesWithoutOther = currentValues.filter(function (v) {
                                    return v !== otherTextValue && baseChoicesValues.includes(v);
                                });
                                if (inputValue) {
                                    field.onChange(__spreadArray(__spreadArray([], valuesWithoutOther, true), [inputValue], false));
                                }
                                else {
                                    field.onChange(__spreadArray(__spreadArray([], valuesWithoutOther, true), [otherTextValue], false));
                                }
                            };
                            var choicesGatedOptions = choicesVisible
                                ? choices
                                : choices.filter(function (option) { return !baseChoicesValues.includes(option.value); });
                            var visibleOptions = element.hideSelectedItems
                                ? choicesGatedOptions.filter(function (option) {
                                    return !autocompleteValue.some(function (selected) { return selected.value === option.value; });
                                })
                                : choicesGatedOptions;
                            return (_jsxs(_Fragment, { children: [_jsx(Autocomplete, { multiple: true, options: visibleOptions, value: autocompleteValue, onChange: handleAutocompleteChange, getOptionDisabled: function (option) {
                                            if (!choicesEnabled &&
                                                baseChoicesValues.includes(option.value)) {
                                                return true;
                                            }
                                            if (option.value === SELECT_ALL_VALUE &&
                                                cannotSelectAll) {
                                                return true;
                                            }
                                            if (!hasReachedMaxSelectedChoices) {
                                                return false;
                                            }
                                            var isAlreadySelected = autocompleteValue.some(function (selectedOption) {
                                                return selectedOption.value === option.value;
                                            });
                                            return !isAlreadySelected;
                                        }, getOptionLabel: function (option) { return option.text; }, isOptionEqualToValue: function (option, value) {
                                            return option.value === value.value;
                                        }, disabled: !isInteractive, disableCloseOnSelect: element.closeOnSelect !== undefined ? false : true, disableClearable: element.allowClear !== undefined ? true : false, renderInput: function (params) {
                                            var _a;
                                            return (_jsx(TextField, __assign({}, params, { error: !!error, placeholder: ((_a = field.value) === null || _a === void 0 ? void 0 : _a.length) === 0
                                                    ? element.placeholder ||
                                                        "Selecione uma ou mais opções"
                                                    : undefined })));
                                        }, renderTags: function (value, getTagProps) {
                                            return value.map(function (option, index) { return (_createElement(Chip, __assign({ label: option.text }, getTagProps({ index: index }), { key: index }))); });
                                        } }), error && (_jsx(FormHelperText, __assign({ error: true, sx: { mt: 1 } }, { children: error.message }))), element.showOtherItem && isOtherSelected && (_jsx(TextField, { fullWidth: true, sx: { mt: 1 }, label: otherTextValue, placeholder: element.otherPlaceholder || "Digite aqui...", value: displayOtherValue, onChange: handleOtherInputChange, disabled: !isInteractive })), element.showCommentArea && (_jsx(CommentArea, { element: element, disabled: !isInteractive }))] }));
                        } })] })) }))) : null }));
};
export default TagboxField;
